<script setup>
import dayjs from 'dayjs'

import { ref, onMounted, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore, searchResultTypeEnum } from '@/stores/web-site.store'
import { useChatStore } from '@/stores/chat.store'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'
import { useCurrency } from '@/composables/currency'
import { useRateCard } from '@/composables/rate-card'
import { useHighlightNewRow } from '@/composables/scroll'
import { formatShortDate } from '@/lib/utils'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConditionTimeline from '@/components/shared/ConditionTimeline.vue'
import EquipmentConditionEdit from '@/components/shared/EquipmentConditionEdit.vue'
import EquipmentEdit from '@/components/shared/EquipmentEdit.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import DescriptionSection from '@/components/shared/DescriptionSection.vue'
import CollapsibleDetailHeader from '@/components/shared/CollapsibleDetailHeader.vue'
import ExpandableContent from '@/components/shared/ExpandableContent.vue'
import AvailabilityCalendar from '@/components/shared/AvailabilityCalendar.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ChipTemplate from './ChipTemplate.vue'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  hideHeader: {
    type: Boolean,
    required: false,
    default: false,
  },
})
useChatStore().trackPageEntity(searchResultTypeEnum.EQUIPMENT, () => props.modelValue?.id)

const { isMobile } = useDetectDevice()
const { t } = getI18n()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const editItem = ref(null)
const { getTagChips, getServiceTagChips, needQrScanChip, isVehicleChip, isSerializedChip, getServiceStructureTypeChip } = useChips()

const infoChips = computed(() => {
  const e = equipment.value
  if (!e) return []
  const chips = []
  if (e.warehouse_name) chips.push({ name: e.warehouse_name, label: e.warehouse_name, color: 'blue-grey', textColor: 'white', size: 'sm' })
  chips.push(...getServiceStructureTypeChip(e.structure_type))
  const serial = [e.serial_number, e.internal_serial].filter(Boolean).join(' / ')
  if (serial) chips.push({ ...isSerializedChip, name: serial, label: serial })
  else if (e.is_serialized) chips.push(isSerializedChip)
  return chips
})
const { getCurrencyString } = useCurrency()

const equipment = ref(props.modelValue || null)
const equipmentStats = ref(null)
const showAddCondition = ref(false)
const conditionsEditMode = ref(false)
const deleteConfirmCondition = ref(null)
const showDeleteConditionDialog = computed({
  get: () => deleteConfirmCondition.value !== null,
  set: (val) => { if (!val) deleteConfirmCondition.value = null },
})
const equipmentConditions = ref([])
const conditionTimelineRef = ref(null)
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const equipmentQrcode = ref(null)
let changed = false

const currentCurrency = ref(webSiteStore.currencies.find(i => i.id === equipment.value.currency_ref) || webSiteStore.currencies.find(i => i.code === equipment.value.currency_ref_code) || webSiteStore.currencies.find(i => i.code === equipment.value.currency?.code) || webSiteStore.getDefaultCurrency())

const rateCard = useRateCard()
const rcUnitSymbol = computed(() => rateCard.unitSymbol(equipment.value))
const rcVatLabel = computed(() => rateCard.vatLabel(equipment.value))
const rcPriceLabel = computed(() => rateCard.priceLabel(equipment.value))
const rcCostLabel = computed(() => rateCard.costLabel(equipment.value))
const hasRateCard = computed(() => rateCard.hasRateCard(equipment.value))
const rentalCompanyName = computed(() => equipment.value?.is_rented ? equipment.value?.rental_company?.name : null)
const chipSize = computed(() => webSiteStore.equipmentDetailHeaderCompact ? 'sm' : 'md')

const dateCostSummary = computed(() => {
  if (!equipmentStats.value || !equipment.value.buy_date) return ''
  return t('common.equipmentStatsDateCost', {
    date: formatShortDate(equipment.value.buy_date),
    cost: getCurrencyString(equipmentStats.value.cost, currentCurrency.value.code),
  })
})

const revenueSummary = computed(() => {
  if (!equipmentStats.value) return ''
  const projects = `${equipmentStats.value.project_count} ${t('common.project', equipmentStats.value.project_count).toLocaleLowerCase()}`

  return t('common.equipmentStatsRevenue', {
    projects,
    revenue: getCurrencyString(equipmentStats.value.revenue, currentCurrency.value.code),
  })
})

const marginSummary = computed(() => {
  if (!equipmentStats.value || equipmentStats.value.roi == null) return
  const margin = getCurrencyString(equipmentStats.value.margin, currentCurrency.value.code)
  const roi = equipmentStats.value.roi != null ? `${equipmentStats.value.roi.toFixed(0)}%` : ''

  return t('common.equipmentStatsMargin', {
    margin,
    roi,
  })
})

onMounted(async() => {
  equipment.value = (await planningStore.fetchEquipmentByIdAsync(equipment.value.id)).data
  await fetchEquipmentConditions()
  if (webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)) equipmentStats.value = (await planningStore.fetchEquipmentStatsByIdAsync(equipment.value.id)).data
})

const fetchEquipmentConditions = async() => {
  equipmentConditions.value = (await planningStore.fetchEquipmentConditionsByEquipmentIdAsync(equipment.value.id)).data.map(condition => {
    const currentService = condition.service_id ? planningStore.getStaffById(condition.service_id) : null
    return {
      ...condition,
      conditionLabel: t(planningStore.equipmentConditionEnumLabels[condition.condition]),
      createdAt: dayjs(condition.created_at).format('YYYY-MM-DD HH:mm'),
      createdByName: currentService ? planningStore.getServiceName(currentService) : condition.username,
      createdByAvatarUrl: currentService?.avatar_url,
    }
  }).sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)))
  .map(condition => ({
    ...condition,
    isDeletable: webSiteStore.canManageOwned(condition, webSiteStore.permissionFlagEnum.MASTER_DATA)
  }))
}

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => { await planningStore.archiveEquipmentAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteEquipmentAsync(id, config) },
})

const onArchive = async(item) => {
  await archiveAsync(item.id, item.archived)
  item.archived = !item.archived
}

const onConditionSaved = async() => {
  showAddCondition.value = false
  const existingIds = new Set(equipmentConditions.value.map(c => c.id))
  await fetchEquipmentConditions()
  changed = true
  const newCondition = equipmentConditions.value.find(c => !existingIds.has(c.id))
  await highlightRowAsync(newCondition?.id, conditionTimelineRef)
}

const onConfirmDeleteCondition = async() => {
  await planningStore.deleteEquipmentConditionAsync(deleteConfirmCondition.value.id)
  deleteConfirmCondition.value = null
  await fetchEquipmentConditions()
  if (!equipmentConditions.value.length) conditionsEditMode.value = false
  changed = true
}

const onNavigateBack = () => {
  if (equipmentQrcode.value !== null) {
    equipmentQrcode.value = null
    return
  }
  changed ? emit('update') : emit('cancel')
}

const emit = defineEmits(['cancel', 'update'])
useNavigator(onNavigateBack)

const onShowQrcode = async() => {
  equipmentQrcode.value = (await planningStore.fetchEquipmentQrcodeByIdAsync(equipment.value.id)).data
}

const reloadEquipmentAsync = async() => {
  equipment.value = (await planningStore.fetchEquipmentByIdAsync(equipment.value.id)).data
  changed = true
  await fetchEquipmentConditions()
}

const onEditItemUpdate = async() => {
  editItem.value = null
  await reloadEquipmentAsync()
}


useEntityChanges([entityChangeTypeEnum.EQUIPMENT], (change) => change.id === equipment.value?.id && reloadEquipmentAsync(), () => editItem.value !== null)
</script>
<template>
  <q-dialog
    :model-value="editItem !== null"
    :maximized="isMobile"
    persistent
    class="dialog-xs"
  >
    <equipment-edit
      v-if="editItem !== null"
      :model-value="editItem"
      @update="onEditItemUpdate"
      @cancel="onEditItemUpdate"
      @delete="editItem = null; emit('update')"
    />
  </q-dialog>
  <archive-warning-dialog
    v-model="archiveWarning"
    @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
  />
  <q-dialog
    :model-value="showAddCondition"
    :maximized="isMobile"
    class="dialog-xs"
    @hide="showAddCondition = false"
  >
    <equipment-condition-edit
      v-if="showAddCondition"
      :equipment="equipment"
      @save="onConditionSaved"
      @cancel="showAddCondition = false"
    />
  </q-dialog>
  <confirm-dialog
    v-model="showDeleteConditionDialog"
    :message="t('common.confirmDelete')"
    icon="mdi-delete-outline"
    icon-color="negative"
    @confirm="onConfirmDeleteCondition"
  />
  <q-card class="column full-height">
    <q-dialog
      :model-value="equipmentQrcode !== null"
      maximized
      persistent
      @hide="equipmentQrcode = null"
    >
      <q-card
        flat
        class="q-ma-none"
        style="max-width: 100vw; max-height: 100vh; display: flex; justify-content: center; align-items: center;"
        @click="equipmentQrcode = null"
      >
        <q-img
          :src="equipmentQrcode?.qr_code"
          class="q-ma-none"
        />
      </q-card>
    </q-dialog>
    <navigate-back
      v-if="!props.hideHeader"
      :text="equipment?.name"
      :archived="equipment?.archived"
      @cancel="onNavigateBack"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-show="!equipment.archived"
            flat
            round
            :disable="equipment.archived"
            icon="mdi-qrcode-scan"
            @click="onShowQrcode()"
          />
          <q-btn
            v-show="!equipment.archived"
            flat
            round
            icon="mdi-pencil"
            @click="editItem = {...equipment}"
          />
          <q-btn
            flat
            round
            :icon="equipment?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
            @click="onArchive(equipment)"
          />
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none" style="min-height: 0;">
      <collapsible-detail-header
        v-model:compact="webSiteStore.equipmentDetailHeaderCompact"
        :avatar="equipment?.avatar_file || equipment?.avatar_url"
        avatar-icon="mdi-camera-off"
      >
        <template #chips>
          <chip-template v-if="infoChips.length" :values="infoChips" :size="chipSize" />
          <chip-template v-if="equipment?.tags?.length" :values="getTagChips(equipment.tags)" :size="chipSize" />
          <chip-template v-if="equipment?.need_qr_scan" :values="[needQrScanChip]" :size="chipSize" />
          <chip-template v-if="equipment?.is_vehicle" :values="[isVehicleChip]" :size="chipSize" />
        </template>
        <template #info>
          <q-list class="q-px-sm">
            <q-item v-if="equipment.code || rentalCompanyName">
              <div v-if="equipment.code" style="width: 50%; float: left;">
                <q-item-section>
                  <q-item-label caption>{{ t('common.code') }}</q-item-label>
                  <q-item-label>{{ equipment.code }}</q-item-label>
                </q-item-section>
              </div>
              <div v-if="rentalCompanyName" style="width: 50%; float: right;">
                <q-item-section style="display: flex; align-content: flex-end;">
                  <q-item-label caption style="text-align: right;">{{ t('common.rentalCompany.name') }}</q-item-label>
                  <q-item-label style="text-align: right;">{{ rentalCompanyName }}</q-item-label>
                </q-item-section>
              </div>
            </q-item>
            <description-section v-if="equipment.description" :text="equipment.description" />
          </q-list>
        </template>
      </collapsible-detail-header>
      <q-list v-if="hasRateCard" class="q-px-sm">
        <q-item v-if="rcPriceLabel || rcCostLabel">
          <div v-if="rcPriceLabel" style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ t('common.price') }}</q-item-label>
              <q-item-label>{{ rcUnitSymbol ? `${rcPriceLabel} / ${rcUnitSymbol}` : rcPriceLabel }}</q-item-label>
            </q-item-section>
          </div>
          <div v-if="rcCostLabel" style="width: 50%; float: right;">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ t('common.cost') }}</q-item-label>
              <q-item-label style="text-align: right;">{{ rcUnitSymbol ? `${rcCostLabel} / ${rcUnitSymbol}` : rcCostLabel }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <q-item v-if="rcUnitSymbol || rcVatLabel">
          <div v-if="rcUnitSymbol" style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ t('common.unitOfMeasure') }}</q-item-label>
              <q-item-label>{{ rcUnitSymbol }}</q-item-label>
            </q-item-section>
          </div>
          <div v-if="rcVatLabel" style="width: 50%; float: right;">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ t('common.vat') }}</q-item-label>
              <q-item-label style="text-align: right;">{{ rcVatLabel }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
      </q-list>
      <div class="q-px-sm q-pb-sm">
        <expandable-content
          always-show-toggle
          :expand-label="t('common.showAvailability')"
          :collapse-label="t('common.hideAvailability')"
        >
          <availability-calendar :service="equipment" />
        </expandable-content>
      </div>
      <q-separator v-if="equipment.tags?.length" class="q-mb-sm" />
      <div v-if="equipment.tags?.length" class="q-px-md q-pb-sm">
        <div v-if="equipment.tags?.length" class="row items-center q-py-xs">
          <div class="col-4 text-caption text-grey-7">{{ t('common.tag', 2) }}</div>
          <div class="col-8"><chip-template :values="getServiceTagChips(equipment.tags)" size="sm" style="margin-right: 0;" /></div>
        </div>
      </div>
      <q-separator v-if="isMobile && webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)" class="q-my-sm" />
      <div v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)" class="q-pa-md">
        <div class="row flex items-center">
          <div class="col-4 q-col-gutter-sm items-center">
            <div class="flex items-center" style="flex-direction: column;">
              <q-icon
                v-if="!equipment.archived"
                name="mdi-chart-box"
                size="lg"
              />
              <div
                v-if="equipmentStats?.roi != null"
                :class="equipmentStats?.roi > 0 ? 'text-positive' : equipmentStats?.roi < 0 ? 'text-negative' : 'grey-5'"
              >
                {{ t('common.roi') }}
              </div>
              <div v-if="equipmentStats?.roi != null" :class="equipmentStats?.roi > 0 ? 'text-positive' : equipmentStats?.roi < 0 ? 'text-negative' : 'grey-5'">
                {{ equipmentStats?.roi?.toFixed(2) }}%
              </div>
            </div>
          </div>
          <div class="col-8 q-col-gutter-sm">
            <div v-if="dateCostSummary">
              {{ dateCostSummary }}
            </div>
            <div v-if="revenueSummary" class="q-mt-sm">
              {{ revenueSummary }}
            </div>
            <div v-if="marginSummary" class="q-mt-sm">
              {{ marginSummary }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="equipment.service_files?.length">
        <q-separator />
        <q-card-section class="q-pt-none">
          <q-list padding>
            <q-item-label header class="header">
              {{ t('common.attachment', equipment.service_files.length) }}
            </q-item-label>
            <file-viewer
              v-for="file in equipment.service_files"
              :key="file.id"
              :mobile="isMobile"
              hide-delete
              :cell-info="{ value: [file] }"
              @download="async(e) => await planningStore.downloadServiceFileAsync(e.id, e.name)"
            />
          </q-list>
        </q-card-section>
      </div>

      <q-separator v-if="isMobile" class="q-my-sm" />
      <div class="row items-center q-px-md q-pt-sm">
        <q-item-label header class="header q-pa-none col">
          {{ t('common.condition', 2) }}
        </q-item-label>
        <q-btn
          v-if="!equipment.archived && equipmentConditions.length"
          class="q-mr-sm"
          flat
          round
          dense
          :icon="conditionsEditMode ? 'mdi-pencil-off' : 'mdi-pencil'"
          :color="conditionsEditMode ? 'primary' : 'grey-6'"
          @click="conditionsEditMode = !conditionsEditMode"
        >
          <q-tooltip>{{ conditionsEditMode ? t('common.done') : t('common.edit') }}</q-tooltip>
        </q-btn>
        <q-btn
          v-if="!equipment.archived"
          style="padding: 0;"
          size="md"
          unelevated
          ripple
          rounded
          icon="mdi-plus-circle-outline"
          @click="showAddCondition = true"
        />
      </div>
      <q-card-section class="q-pt-none">
        <condition-timeline
          ref="conditionTimelineRef"
          class="q-px-sm"
          :conditions="equipmentConditions"
          :highlighted-id="highlightedRowId"
          :condition-colors="planningStore.equipmentConditionColors"
          :edit-mode="conditionsEditMode && !equipment.archived"
          @delete="deleteConfirmCondition = $event"
        />
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
